import {
  // eslint-disable-next-line import/named
  createRootRouteWithContext,
  Outlet,
  useSearch,
  useRouter,
} from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";

import { CustomErrorComponent } from "./customErrorComponent";
import { NotFoundComponent } from "./notFoundComponent";

import { MobileRedirectPage } from "../../components/MobileRedirectPage";
import { AnalyticsProvider } from "../../utils/analytics";
import { useAppSelector } from "../../utils/hooks";
import { isPhoneSizedDevice } from "../../utils/mobileDetection";
import { searchParser } from "../../utils/parsers/search";
import { useEnterprises } from "../../utils/queries/enterprises";
import { type queryClient } from "../../utils/queryClient";
import { derive } from "../../utils/slices/auth";
import { store } from "../../utils/store";
import { generateUUID } from "../../utils/uuid";

export const rootRoute = createRootRouteWithContext<{
  queryClient: typeof queryClient;
}>()({
  validateSearch(search: Record<string, unknown>) {
    return searchParser.parse(search);
  },
  beforeLoad() {
    store.dispatch(derive());
  },
  component: function Component() {
    const user = useAppSelector((state) => state.auth.value);
    const posthog = usePostHog();
    const search = useSearch({ strict: false });
    const router = useRouter();
    // Track the query, not just the data, or the component won't rerender when the query completes
    const enterprisesQuery = useEnterprises();

    const [isPhoneSize, setIsPhoneSize] = useState<boolean | null>(null);

    // Check if device is phone-sized on mount and window resize
    useEffect(() => {
      const checkPhoneSize = () => setIsPhoneSize(isPhoneSizedDevice());

      checkPhoneSize();
      window.addEventListener("resize", checkPhoneSize);

      return () => window.removeEventListener("resize", checkPhoneSize);
    }, []);

    const currentEnterprise = enterprisesQuery.data?.find(
      (e) => e._id === user?.enterpriseId,
    );

    // @ts-expect-error TS2339: `window` is strictly typed
    window.UnsafeDeps = Object.assign({}, window.UnsafeDeps, {
      user,
      enterprise: currentEnterprise,
    });

    useEffect(() => {
      if (user?.userID) {
        posthog?.identify(user.userID, {
          email: user.email,
          name: user.name,
          role: user.role,
          enterpriseId: currentEnterprise?._id ?? user.enterpriseId,
          enterpriseName:
            currentEnterprise?.name ?? user.enterpriseName ?? user.company,
          visitId: generateUUID(),
        });

        posthog?.group(
          "enterprise",
          currentEnterprise?._id ?? (user.enterpriseId as string),
        );
      }
    }, [posthog, user, enterprisesQuery]);

    // Wait for phone size detection to complete before rendering
    if (isPhoneSize === null) {
      return null;
    }

    // Show mobile redirect page if on phone-sized device AND user is logged in
    if (isPhoneSize && user != null) {
      return <MobileRedirectPage />;
    }

    // remove the token from the URL if it is a shared ticket token
    if (search.t != null && (!user?.ticketId || !user?.folderId)) {
      const { t: _t, ...rest } = search;
      router.navigate({
        to: router.state.location.pathname,
        search: rest,
        replace: true,
      });
      return null;
    }

    return (
      <AnalyticsProvider>
        <Outlet />
      </AnalyticsProvider>
    );
  },
  errorComponent: CustomErrorComponent,
  notFoundComponent: NotFoundComponent,
});
